import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import { getAllFeeTypes } from '../../api/fees';
import { DraperyModule } from '../../redux/modules';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox-shadcn';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import {
  normalizeFeeQuantityInput,
  normalizeFeeUnitPriceInput,
} from './order-fees-section.utils';

type FeeTypeOption = {
  id: string | number;
  label: string;
  defaultUnitPrice: number;
};

type DisplayFeeRow = DraperyModule.DraperyOrderFee & {
  rowKey: string;
  feeTypeName: string;
  description: string;
  quantity: string;
  unitPrice: string;
};

interface OrderFeesSectionProps {
  accountId: number;
  clientId?: number | null;
  clientBillingType?: string | null;
  orderId: number;
  fees: DraperyModule.DraperyOrderFee[];
  showBundleColumn: boolean;
  defaultBundleChecked: boolean;
  clientAdjustmentType?: string | null;
  clientAdjustmentValue?: number | string | null;
  disabled?: boolean;
  onChange?(fees: DraperyModule.DraperyOrderFee[]): void;
  onBundleChange?(fees: DraperyModule.DraperyOrderFee[]): void;
  onBundledTotalChange?(value: number): void;
}

const DEFAULT_FEE_TYPES: FeeTypeOption[] = [
  { id: 'measure', label: 'Measure', defaultUnitPrice: 0 },
  { id: 'install', label: 'Install', defaultUnitPrice: 0 },
  { id: 'consult', label: 'Consult', defaultUnitPrice: 0 },
];

const resolveFeeTypeDefaultUnitPrice = (feeType: any, clientBillingType?: string | null): number => {
  let amountRetail = '';
  let amountTrade = '';

  if (feeType?.retailPricingMethod === 'markUpCostRetail') {
    amountRetail = (
      Number(feeType.cost || 0) +
      (Number(feeType.cost || 0) * Number(feeType.retailPricingValue || 0)) / 100
    ).toFixed(2);
  }
  if (feeType?.retailPricingMethod === 'discountMsrpRetail') {
    amountRetail = (
      Number(feeType.msrp || 0) -
      (Number(feeType.msrp || 0) * Number(feeType.retailPricingValue || 0)) / 100
    ).toFixed(2);
  }
  if (feeType?.retailPricingMethod === 'costPlusRetail') {
    amountRetail = (
      Number(feeType.cost || 0) + Number(feeType.retailPricingValue || 0)
    ).toFixed(2);
  }
  if (feeType?.retailPricingMethod === 'rateRetail') {
    amountRetail = Number(feeType.retailPricingValue || 0).toFixed(2);
  }

  if (feeType?.tradePricingMethod === 'costPlusTrade') {
    amountTrade = (
      Number(feeType.cost || 0) + Number(feeType.tradePricingValue || 0)
    ).toFixed(2);
  }
  if (feeType?.tradePricingMethod === 'markUpCostTrade') {
    amountTrade = (
      Number(feeType.cost || 0) +
      (Number(feeType.cost || 0) * Number(feeType.tradePricingValue || 0)) / 100
    ).toFixed(2);
  }
  if (feeType?.tradePricingMethod === 'discountMsrpTrade') {
    amountTrade = (
      Number(feeType.msrp || 0) -
      (Number(feeType.msrp || 0) * Number(feeType.tradePricingValue || 0)) / 100
    ).toFixed(2);
  }
  if (feeType?.tradePricingMethod === 'rateTrade') {
    amountTrade = Number(feeType.tradePricingValue || 0).toFixed(2);
  }

  const resolvedValue =
    clientBillingType === 'Trade'
      ? amountTrade
        ? Number(amountTrade)
        : 0
      : amountRetail
        ? Number(amountRetail)
        : 0;

  return Number.isFinite(resolvedValue) ? resolvedValue : 0;
};

const parseNumber = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const normalizedValue = `${value || ''}`.replace(/[^0-9.-]/g, '');
  const parsedValue = Number(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const parseFeeTypeId = (value: FeeTypeOption['id']): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatAdjustment = (type?: string | null, value?: number | string | null): string => {
  const parsedValue = parseNumber(value);

  if (type === 'markup') {
    return `+${parsedValue}%`;
  }

  if (type === 'discount') {
    return `-${parsedValue}%`;
  }

  return '';
};

const getAdjustmentMultiplier = (type?: string | null, value?: number | string | null): number => {
  const parsedValue = parseNumber(value);

  if (type === 'markup') {
    return 1 + parsedValue / 100;
  }

  if (type === 'discount') {
    return 1 - parsedValue / 100;
  }

  return 1;
};

const createFeeRow = ({
  clientAdjustmentType,
  clientAdjustmentValue,
  defaultBundleChecked,
  showBundleColumn,
}: {
  clientAdjustmentType?: string | null;
  clientAdjustmentValue?: number | string | null;
  defaultBundleChecked: boolean;
  showBundleColumn: boolean;
}): DraperyModule.DraperyOrderFee => ({
  feeTypeId: null,
  feeTypeName: null,
  description: '',
  quantity: 1,
  unitPrice: '0.00',
  clientPriceAdjustmentType: clientAdjustmentType || null,
  clientPriceAdjustmentValue:
    clientAdjustmentValue === null || clientAdjustmentValue === undefined || clientAdjustmentValue === ''
      ? null
      : parseNumber(clientAdjustmentValue),
  isBundled: showBundleColumn ? defaultBundleChecked : false,
});

const withDerivedFeeValues = (
  fee: DraperyModule.DraperyOrderFee,
  clientAdjustmentType?: string | null,
  clientAdjustmentValue?: number | string | null
): DraperyModule.DraperyOrderFee => ({
  ...fee,
  clientPriceAdjustmentType: clientAdjustmentType || null,
  clientPriceAdjustmentValue:
    clientAdjustmentValue === null || clientAdjustmentValue === undefined || clientAdjustmentValue === ''
      ? null
      : parseNumber(clientAdjustmentValue),
  isBundled: !!fee.isBundled,
});

const OrderFeesSection: React.FC<OrderFeesSectionProps> = ({
  accountId,
  clientId,
  clientBillingType,
  fees,
  showBundleColumn,
  defaultBundleChecked,
  clientAdjustmentType,
  clientAdjustmentValue,
  disabled = false,
  onChange,
  onBundleChange,
  onBundledTotalChange,
}) => {
  const [feeTypes, setFeeTypes] = useState<FeeTypeOption[]>(DEFAULT_FEE_TYPES);

  useEffect(() => {
    let isActive = true;

    const loadFeeTypes = async () => {
      if (!accountId || !clientId) {
        if (isActive) {
          setFeeTypes(DEFAULT_FEE_TYPES);
        }
        return;
      }

      try {
        const response = await getAllFeeTypes({
          accountId,
          clientId,
        });

        if (!isActive) return;

        if (Array.isArray(response) && response.length > 0) {
          setFeeTypes(
            response.map((feeType: any) => ({
              id: feeType.id,
              label: feeType.name,
              defaultUnitPrice: resolveFeeTypeDefaultUnitPrice(feeType, clientBillingType),
            }))
          );
          return;
        }

        setFeeTypes(DEFAULT_FEE_TYPES);
      } catch (error) {
        console.error('Failed to load fee types for order summary fees.', error);
        if (isActive) {
          setFeeTypes(DEFAULT_FEE_TYPES);
        }
      }
    };

    void loadFeeTypes();

    return () => {
      isActive = false;
    };
  }, [accountId, clientBillingType, clientId]);


  const rows = useMemo<DisplayFeeRow[]>(
    () =>
      (Array.isArray(fees) ? fees : []).map((fee, index) => ({
        ...fee,
        rowKey: fee.id ? `fee-${fee.id}` : `new-fee-${index}`,
        feeTypeName: fee.feeTypeName || '',
        description: fee.description || '',
        quantity:
          fee.quantity === null || fee.quantity === undefined || fee.quantity === ''
            ? ''
            : normalizeFeeQuantityInput(String(fee.quantity)),
        unitPrice:
          fee.unitPrice === null || fee.unitPrice === undefined
            ? '0.00'
            : normalizeFeeUnitPriceInput(String(fee.unitPrice)),
        isBundled: !!fee.isBundled,
      })),
    [fees]
  );

  const showClientAdjustmentColumn =
    (clientAdjustmentType === 'markup' || clientAdjustmentType === 'discount') &&
    Number.isFinite(parseNumber(clientAdjustmentValue)) &&
    parseNumber(clientAdjustmentValue) !== 0;

  const clientAdjustmentDisplay = formatAdjustment(clientAdjustmentType, clientAdjustmentValue);
  const adjustmentMultiplier = getAdjustmentMultiplier(clientAdjustmentType, clientAdjustmentValue);

  const rowsWithTotals = useMemo(
    () =>
      rows.map((row) => {
        const total = parseNumber(row.quantity) * parseNumber(row.unitPrice) * adjustmentMultiplier;

        return {
          ...row,
          total,
        };
      }),
    [adjustmentMultiplier, rows]
  );

  useEffect(() => {
    if (!onBundledTotalChange) return;

    const bundledTotal = showBundleColumn
      ? rowsWithTotals.reduce((sum, row) => sum + (row.isBundled ? row.total : 0), 0)
      : 0;

    onBundledTotalChange(bundledTotal);
  }, [onBundledTotalChange, rowsWithTotals, showBundleColumn]);

  const updateRows = (nextFees: DraperyModule.DraperyOrderFee[]) => {
    if (onChange) {
      onChange(nextFees);
    }
  };

  const updateFeeAtIndex = (
    feeIndex: number,
    updater: (fee: DraperyModule.DraperyOrderFee) => DraperyModule.DraperyOrderFee
  ) => {
    updateRows(
      (Array.isArray(fees) ? fees : []).map((fee, index) =>
        index === feeIndex
          ? withDerivedFeeValues(
              updater(fee),
              clientAdjustmentType,
              clientAdjustmentValue
            )
          : fee
      )
    );
  };

  const handleAddFee = (feeType: FeeTypeOption) => {
    updateRows([
      ...(Array.isArray(fees) ? fees : []),
      {
        ...createFeeRow({
          clientAdjustmentType,
          clientAdjustmentValue,
          defaultBundleChecked,
          showBundleColumn,
        }),
        feeTypeId: parseFeeTypeId(feeType.id),
        feeTypeName: feeType.label,
        unitPrice: feeType.defaultUnitPrice.toFixed(2),
      },
    ]);
  };

  const handleFeeTypeSelect = (feeIndex: number, feeType: FeeTypeOption) => {
    updateFeeAtIndex(feeIndex, (fee) => ({
      ...fee,
      feeTypeId: parseFeeTypeId(feeType.id),
      feeTypeName: feeType.label,
      unitPrice: feeType.defaultUnitPrice.toFixed(2),
    }));
  };

  const handleDeleteRow = (feeIndex: number) => {
    updateRows((Array.isArray(fees) ? fees : []).filter((_, index) => index !== feeIndex));
  };

  return (
    <div className="w-full min-w-0 self-stretch">
      <div className="w-full min-w-0 rounded-[14px] border border-input bg-white">
          <Table
            wrapperClassName="max-h-none max-w-full rounded-[14px] border-0"
            className={showBundleColumn ? 'min-w-[980px]' : 'min-w-[860px]'}
          >
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-auto bg-[#ECF6F9] px-4 py-4 text-[#3B97B1] shadow-none">Fee Type</TableHead>
                <TableHead className="h-auto bg-[#ECF6F9] px-4 py-4 text-[#3B97B1] shadow-none">Description</TableHead>
                <TableHead className="h-auto bg-[#ECF6F9] px-4 py-4 text-left text-[#3B97B1] shadow-none">Qty</TableHead>
                <TableHead className="h-auto bg-[#ECF6F9] px-4 py-4 text-[#3B97B1] shadow-none">Unit Price ($)</TableHead>
                {showClientAdjustmentColumn && (
                  <TableHead className="h-auto bg-[#ECF6F9] px-4 py-4 text-right text-[#3B97B1] shadow-none">
                    Client Price Adj.
                  </TableHead>
                )}
                <TableHead className="h-auto bg-[#ECF6F9] px-4 py-4 text-right text-[#3B97B1] shadow-none">Total Price ($)</TableHead>
                {showBundleColumn && (
                  <TableHead className="h-auto bg-[#ECF6F9] px-4 py-4 text-center text-[#3B97B1] shadow-none">Bundle</TableHead>
                )}
                <TableHead className="h-auto w-[56px] bg-[#ECF6F9] px-4 py-4 shadow-none" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={99} className="px-6 py-8 text-center text-[16px] italic text-muted-foreground">
                    No fees added. Click "+ Add Fee" to add one.
                  </td>
                </tr>
              ) : rowsWithTotals.map((row, rowIndex) => {
                const selectedFeeType = feeTypes.find(
                  (item) => String(item.id) === String(row.feeTypeId || '')
                );
                const rowKey = row.id ? `fee-${row.id}` : row.rowKey;

                return (
                  <TableRow key={rowKey} className="hover:bg-white">
                    <TableCell className="px-4 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild disabled={disabled}>
                          <Button
                            variant="outline"
                            disabled={disabled}
                            className="h-10 min-w-[140px] justify-between px-3 text-[13px] font-normal"
                            data-qa={`order-fees-row-fee-type-${rowKey}`}
                          >
                            <span
                              className={
                                selectedFeeType || row.feeTypeName
                                  ? 'text-foreground'
                                  : 'text-muted-foreground'
                              }
                            >
                              {selectedFeeType?.label || row.feeTypeName || 'Select'}
                            </span>
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="min-w-[180px]">
                          {feeTypes.map((feeType) => (
                            <DropdownMenuItem
                              key={`${rowKey}-${feeType.id}`}
                              onSelect={() => handleFeeTypeSelect(rowIndex, feeType)}
                            >
                              <div className="flex w-full items-center justify-between gap-3">
                                <span>{feeType.label}</span>
                                {String(row.feeTypeId || '') === String(feeType.id) && (
                                  <Check className="h-4 w-4 text-[#3B97B1]" />
                                )}
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>

                    <TableCell className="px-4 py-4">
                      <Input
                        value={row.description}
                        placeholder="Description"
                        disabled={disabled}
                        onChange={(event) =>
                          updateFeeAtIndex(rowIndex, (currentFee) => ({
                            ...currentFee,
                            description: event.target.value,
                          }))
                        }
                        className="h-10 min-w-[220px] text-[13px]"
                        data-qa={`order-fees-row-description-${rowKey}`}
                      />
                    </TableCell>

                    <TableCell className="px-4 py-4">
                      <Input
                        type="number"
                        min="1"
                        step="0.01"
                        value={row.quantity}
                        disabled={disabled}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        onChange={(event) => {
                          const val = normalizeFeeQuantityInput(event.target.value);
                          if (val !== '' && Number(val) < 1) return;
                          updateFeeAtIndex(rowIndex, (currentFee) => ({
                            ...currentFee,
                            quantity: val,
                          }));
                        }}
                        onBlur={(event) => {
                          if (event.target.value === '' || Number(event.target.value) < 1) {
                            updateFeeAtIndex(rowIndex, (currentFee) => ({
                              ...currentFee,
                              quantity: 1,
                            }));
                          }
                        }}
                        className="h-10 w-[72px] text-center text-[13px]"
                        data-qa={`order-fees-row-qty-${rowKey}`}
                      />
                    </TableCell>

                    <TableCell className="px-4 py-4">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.unitPrice}
                        disabled={disabled}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        onChange={(event) => {
                          const val = normalizeFeeUnitPriceInput(event.target.value);
                          if (val !== '' && Number(val) < 0) return;
                          updateFeeAtIndex(rowIndex, (currentFee) => ({
                            ...currentFee,
                            unitPrice: val,
                          }));
                        }}
                        onBlur={(event) => {
                          if (event.target.value === '') {
                            updateFeeAtIndex(rowIndex, (currentFee) => ({
                              ...currentFee,
                              unitPrice: '0.00',
                            }));
                          }
                        }}
                        className="h-10 w-[120px] text-right text-[13px]"
                        data-qa={`order-fees-row-unit-price-${rowKey}`}
                      />
                    </TableCell>

                    {showClientAdjustmentColumn && (
                      <TableCell className="px-4 py-4 text-right text-[13px] text-[#64748B]">
                        <span data-qa={`order-fees-row-client-adjustment-${rowKey}`}>
                          {clientAdjustmentDisplay}
                        </span>
                      </TableCell>
                    )}

                    <TableCell className="px-4 py-4 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-[14px] font-semibold" data-qa={`order-fees-row-total-${rowKey}`}>
                          ${formatCurrency(row.total)}
                        </span>
                        {showBundleColumn && row.isBundled && (
                          <span className="text-[11px] italic text-[#3B97B1]">
                            Included in bundle price
                          </span>
                        )}
                      </div>
                    </TableCell>

                    {showBundleColumn && (
                      <TableCell className="px-4 py-4">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={row.isBundled}
                            onCheckedChange={(checked) => {
                              const nextFees = (Array.isArray(fees) ? fees : []).map((fee, index) =>
                                index === rowIndex
                                  ? withDerivedFeeValues(
                                      { ...fee, isBundled: Boolean(checked) },
                                      clientAdjustmentType,
                                      clientAdjustmentValue
                                    )
                                  : fee
                              );
                              if (onChange) {
                                onChange(nextFees);
                              }
                              if (onBundleChange) {
                                onBundleChange(nextFees);
                              }
                            }}
                            data-qa={`order-fees-row-bundle-${rowKey}`}
                          />
                        </div>
                      </TableCell>
                    )}

                    <TableCell className="px-4 py-4">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        disabled={disabled}
                        onClick={() => handleDeleteRow(rowIndex)}
                        className="text-[#64748B] hover:text-[#334155]"
                        data-qa={`order-fees-row-delete-${rowKey}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
      </div>

      <div className="px-0 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <Button
              type="button"
              variant="ghost"
              disabled={disabled}
              className="h-auto min-h-0 gap-2 p-0 text-[16px] font-medium text-[#3B97B1] hover:bg-transparent hover:text-[#2E8197]"
              data-qa="order-fees-add-fee"
            >
              <Plus className="h-4 w-4" />
              Add Fee
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" className="min-w-[180px]">
            {feeTypes.length > 0 ? (
              feeTypes.map((feeType) => (
                <DropdownMenuItem
                  key={`add-fee-${feeType.id}`}
                  onSelect={() => handleAddFee(feeType)}
                  data-qa={`order-fees-add-fee-option-${feeType.id}`}
                >
                  {feeType.label}
                </DropdownMenuItem>
              ))
            ) : (
              <DropdownMenuItem disabled>No Fees</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default OrderFeesSection;
